import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const DataTableWidget = ({ config = {} }) => {
  const title = config.title || 'Top Pages';
  const striped = config.striped !== false;
  const bordered = config.bordered !== false;
  const compact = config.compact === true;
  const headerStyle = config.headerStyle || 'dark';
  const rowCount = config.rowCount || 10;

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const columns = config.columns || [
    { key: 'page', label: 'Page', type: 'text' },
    { key: 'sessions', label: 'Sessions', type: 'number' },
    { key: 'bounce_rate', label: 'Bounce Rate', type: 'percentage' },
    { key: 'avg_duration', label: 'Avg. Duration', type: 'text' },
    { key: 'conversions', label: 'Conversions', type: 'number' }
  ];

  const demoRows = config.data || [];

  if (!demoRows.length) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center text-center p-4">
        <div className="text-sm text-gray-500">No data source connected</div>
        <div className="text-xs text-gray-400 mt-1">Connect a source to populate this table.</div>
      </div>
    );
  }

  const data = demoRows.slice(0, rowCount);

  const sorted = [...data].sort((a, b) => {
    if (!sortKey) return 0;
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const headerColors = {
    dark: 'bg-gray-800 text-white',
    blue: 'bg-blue-600 text-white',
    light: 'bg-gray-100 text-gray-700',
    minimal: 'bg-white text-gray-600 border-b-2 border-gray-200'
  };

  const formatCell = (value, type) => {
    if (type === 'number') return typeof value === 'number' ? value.toLocaleString() : value;
    if (type === 'percentage') return typeof value === 'number' ? value + '%' : value;
    return value;
  };

  return (
    <div className="h-full flex flex-col bg-white p-3">
      <h3 className="text-sm font-semibold text-gray-700 mb-2" style={{ color: config.titleColor || '#374151' }}>
        {title}
      </h3>
      <div className="flex-1 overflow-auto">
        <table className={`w-full text-left ${bordered ? 'border border-gray-200' : ''}`}>
          <thead>
            <tr className={headerColors[headerStyle] || headerColors.dark}>
              {columns.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)}
                  className={`${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-xs'} font-semibold cursor-pointer select-none whitespace-nowrap`}>
                  <div className="flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, idx) => (
              <tr key={idx} className={`${bordered ? 'border-b border-gray-100' : ''} ${striped && idx % 2 === 1 ? 'bg-gray-50' : ''} hover:bg-blue-50/50 transition-colors`}>
                {columns.map(col => (
                  <td key={col.key} className={`${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} text-gray-600`}>
                    {formatCell(row[col.key], col.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTableWidget;
